import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {TranslateModule} from '@ngx-translate/core';
import {CommonModule} from '@angular/common';
import {MatListModule} from '@angular/material/list';
import {MatDialogModule, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {DialogCloseButtonComponent, SecondaryButtonComponent} from '@q9elements/ui-kit/components';
import {getFullName, Q9HtmlDecoderPipe} from '@q9elements/ui-kit/common';
import {StickersService} from '../../../diagram-interaction/services/stickers.service';
import {DiagramJsonHandlerService} from '../../../diagram-data/diagram-json-handler.service';
import {DraggableDialogDirective} from '../../shared/directives/draggable-dialog.directive';

type NoteFilter = 'author' | 'paperClass' | 'tags';

@Component({
  selector: 'app-filter-notes-dialog',
  templateUrl: './filter-notes-dialog.component.html',
  styleUrls: ['./filter-notes-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    DraggableDialogDirective,
    MatListModule,
    TranslateModule,
    DialogCloseButtonComponent,
    Q9HtmlDecoderPipe,
    SecondaryButtonComponent,
    MatSlideToggleModule
  ]
})
export class FilterNotesDialogComponent implements OnInit, OnDestroy {
  allResources: any[] = [];
  hiddenProps = {author: {}, paperClass: {}, tags: {}};
  diagramJsonSubscr: Subscription;
  constructor(
    private stickersSvc: StickersService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    @Inject(MAT_DIALOG_DATA) public data: {filterProp: NoteFilter}
  ) {}

  ngOnInit() {
    this.diagramJsonSubscr = this.diagramJsonSvc.diagramJson$.subscribe(json => {
      const notes = json?.notes || [];

      this.hiddenProps = this.stickersSvc.listHiddenProps;
      this.calculateResources(notes, this.data.filterProp);
    });
  }

  ngOnDestroy() {
    this.diagramJsonSubscr.unsubscribe();
  }

  toggleSelect(option: any) {
    if (this.hiddenProps[this.data.filterProp][option.key]) {
      delete this.hiddenProps[this.data.filterProp][option.key];
    } else {
      this.hiddenProps[this.data.filterProp][option.key] = option;
    }

    this.stickersSvc.filterNotes(this.hiddenProps);
  }

  isSelected(resource) {
    return !this.hiddenProps[this.data.filterProp][resource.key];
  }

  resetSelected() {
    if (!Object.values(this.hiddenProps[this.data.filterProp]).length) {
      return;
    }

    this.hiddenProps[this.data.filterProp] = {};
    this.stickersSvc.filterNotes(this.hiddenProps);
  }

  private calculateResources(notes = [], filterProp: string) {
    switch (filterProp) {
      case 'author':
        return this.calculateAuthors(notes);

      case 'paperClass':
        return this.calculateColors(notes);

      case 'tags':
        return this.calculateTags(notes);
    }
  }

  private calculateAuthors(notes = []) {
    const map = notes.reduce((acc, note) => {
      const {author} = note;

      if (author) {
        if (!acc[author.id]) {
          acc[author.id] = {
            key: author.id,
            count: 1,
            fullName: getFullName(author)
          };
        } else {
          acc[author.id].count++;
        }
      }

      return acc;
    }, {});

    this.allResources = Object.values(map);
  }

  private calculateColors(notes = []) {
    const map = notes.reduce((acc, note) => {
      const {paperClass} = note;

      if (paperClass) {
        if (!acc[paperClass]) {
          acc[paperClass] = {
            key: paperClass,
            count: 1
          };
        } else {
          acc[paperClass].count++;
        }
      }

      return acc;
    }, {});

    this.allResources = Object.values(map);
  }

  private calculateTags(notes = []) {
    const map = notes.reduce((acc, note) => {
      const {tags} = note;

      if (tags) {
        tags.forEach(tag => {
          if (!acc[tag]) {
            acc[tag] = {
              key: tag,
              count: 1
            };
          } else {
            acc[tag].count++;
          }
        });
      }

      return acc;
    }, {});

    this.allResources = Object.values(map);
  }

  get isResetDisabled() {
    return Object.keys(this.hiddenProps[this.data.filterProp]).length === 0;
  }
}
